import { type RootState } from 'reducers/root';
import { type Middleware } from 'redux';
import merge from 'deepmerge';
import isSubset from 'is-subset';
import { type PersistedState } from 'persistence/types';
import persistenceKey, { persistenceId } from 'persistence/persistenceKey';
import initialState from 'persistence/initialState';
import {
  clear,
  clearAllByApiKey,
  getStateFromLocalStorage,
  persist,
} from './localStorage';
import {
  disablePersistence,
  selectIsCurrentTabPrimary,
} from './persistenceBetweenTabs';
import { mergeDeepExceptArrays, omit } from './utils';
import getPersistenceParameters from './getPersistenceParameters';
import { isObjectEmpty } from 'utils/utils';
import {
  selectPersistenceDurationMilliseconds,
  selectUpdatedAtMilliseconds,
} from 'reducers/workflows';

export const defaultPersistenceDurationMinutes = 180; // 3 hours

// "Persistence window" check:
function isExpired(localStorageState: PersistedState): boolean {
  const persistenceDurationMilliseconds =
    selectPersistenceDurationMilliseconds(localStorageState);
  const updatedAtMilliseconds = selectUpdatedAtMilliseconds(localStorageState);

  return Date.now() - updatedAtMilliseconds > persistenceDurationMilliseconds;
}

/**
 * Creates an object with keys derived from a pipe-delimited parameter string
 * where each key maps to undefined, for use with the omit function
 *
 * @param persistenceParameters - The parameters object containing configuration
 * @param parameterName - The name of the parameter containing pipe-delimited values
 * @returns Record with attributes and their lowercase variants as keys with undefined values
 */
function getParametersToOmit(
  persistenceParameters: Record<string, string | null>,
  parameterName: string,
): Record<string, undefined> {
  if (!persistenceParameters[parameterName]) {
    return {};
  }

  return Object.fromEntries<undefined>(
    persistenceParameters[parameterName].split('|').flatMap(attribute => [
      [attribute, undefined],
      [attribute.toLowerCase(), undefined],
    ]),
  );
}

function isContextChanged(localStorageState: PersistedState): boolean {
  const persistenceParameters = getPersistenceParameters();
  const relevantPersistenceParameters = omit(persistenceParameters, {
    // We want to filter out `config-ft-persistence-id` and
    // `data-ft-workflow-tag` because they are a part of persistence key:
    ...(persistenceId
      ? { 'config-ft-persistence-id': undefined }
      : { 'data-ft-workflow-tag': undefined }),

    // hardcoded for elementor for 1 day, should be removed
    'data-ft-jwt-token': undefined,
    // We want to filter out `src` because some customers use cache busting
    // which might interfere with persistence:
    src: undefined,

    // Filter out all ignored persistence parameters:
    ...getParametersToOmit(
      persistenceParameters,
      'config-ft-ignore-persistence-parameters',
    ),
    ...getParametersToOmit(
      persistenceParameters,
      'config-ft-ignore-and-update-persistence-parameters',
    ),
  });

  return !isSubset(
    localStorageState.config?.rawParams || {},
    relevantPersistenceParameters,
  );
}

// Use this function to modify localStorage state before it gets merged with
// initial state:
function normalizeLocalStorageStateBeforeMergingWithInitialState(
  localStorageState: PersistedState,
): PersistedState {
  const normalizedLocalStorageState = omit(localStorageState, {
    main: {
      isMobile: undefined,
    },
    workflows: {
      isFloatingBarFocused: undefined,
      persistedLiveChatComponents: undefined,
    },
  });
  const emptyState: PersistedState =
    normalizedLocalStorageState.workflows?.enabled_feature_flags?.includes(
      'remember_privacy_policy_consent',
    )
      ? {
          workflows: {
            // `consentedPrivacyPolicyHash` has to be persisted even when we
            // reset the widget:
            consentedPrivacyPolicyHash:
              normalizedLocalStorageState.workflows
                ?.consentedPrivacyPolicyHash ??
              initialState.workflows.consentedPrivacyPolicyHash,
          },
        }
      : {};

  if (isExpired(normalizedLocalStorageState)) {
    return emptyState;
  }

  if (normalizedLocalStorageState.workflows?.error) {
    return emptyState;
  }

  /**
   * If user left the page while the widget was loading, or
   * we do not have the widget-config in local storage, reset the widget.
   */
  if (
    normalizedLocalStorageState.workflows?.loading ||
    normalizedLocalStorageState.workflows?.streaming ||
    !normalizedLocalStorageState.workflows?.workflowsConfig
  ) {
    return emptyState;
  }

  if (isContextChanged(normalizedLocalStorageState)) {
    return emptyState;
  }

  // Always hide widget on load for mobile screens (except when
  // `full-screen="true"` is used). This is needed for the case when user
  // opens an internal link (in this case user wants to see the page, not the
  // widget).
  if (localStorageState.main?.isMobile) {
    return merge<PersistedState, PersistedState>(normalizedLocalStorageState, {
      main: {
        isHelpButtonVisible: true,
        isWidgetVisible: false,
      },
    });
  }

  return normalizedLocalStorageState;
}

export const persistMiddleware: Middleware<unknown, RootState> =
  store => next => action => {
    // Sometimes `action` is `undefined`, and Redux throws
    // "Uncaught Error: Actions must be plain objects."
    // This check will prevent the error:
    if (!action) {
      return;
    }

    // Have to call `next(action)` FIRST, so that `store.getState()` call a few
    // lines below would return the new state:
    next(action);

    const state = store.getState();

    const isFulfilledAction =
      typeof action === 'object' &&
      'type' in action &&
      typeof action.type === 'string' &&
      action.type.endsWith('/fulfilled');

    if (
      !persistenceKey ||
      // Even if the current tab is not primary, do persist if the action is a
      // `/fulfilled` action, to prevent inconsistent state when switching tabs
      // in the middle of network requests:
      (!selectIsCurrentTabPrimary(state) && !isFulfilledAction)
    ) {
      return;
    }

    if (disablePersistence.match(action)) {
      // When disabling persistence, we want to call `persist()` one last time,
      // to send the new state to other tabs:
      persist(state);

      clearAllByApiKey();

      return;
    }

    if (state.workflows.workflowsConfig?.persistence_enabled) {
      persist(state);
    } else {
      clear();
    }
  };

export function getPreloadedState(): RootState {
  const localStorageState = getStateFromLocalStorage();
  const normalizedLocalStorageState =
    normalizeLocalStorageStateBeforeMergingWithInitialState(localStorageState);

  // If necessary, reset persisted state. It will be broadcast to other tabs.
  if (
    localStorageState.workflows?.workflowsConfig?.persistence_enabled &&
    isObjectEmpty(normalizedLocalStorageState)
  ) {
    persist(initialState);
  }

  return mergeDeepExceptArrays(initialState, normalizedLocalStorageState);
}
